/**
 * DocumentCheckMatrix - Table/matrix for controlling which document types
 * participate in single-check, cross-check, and issue creation.
 *
 * Reads extraction settings from parent props (no extra API calls).
 * Writes directly via apiPut (no query invalidation → no parent panel re-fetch).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, CheckboxBase } from "@/components/base/checkbox/checkbox";
import { Badge } from "@/components/base/badges/badges";
import { apiGet, apiPut } from "@/lib/api-client";
import { toast } from "@/lib/toast-service";
import { useDocumentTypes } from "@/hooks/api/use-document-types";
import type { Setting } from "@/types/api/settings";

// Hidden/utility doc types to exclude from the matrix
const HIDDEN_DOC_TYPES = ["not_useful", "not_related", "other"];

type ColumnKey = "single_check" | "cross_check" | "issue_creation";

interface ColumnConfig {
    key: ColumnKey;
    settingCategory: string;
    settingKey: string;
    label: string;
}

const COLUMNS: ColumnConfig[] = [
    { key: "single_check", settingCategory: "extraction", settingKey: "single_check_doc_types", label: "Single Check" },
    { key: "cross_check", settingCategory: "extraction", settingKey: "cross_check_doc_types", label: "Cross Check" },
    { key: "issue_creation", settingCategory: "extraction", settingKey: "issue_creation_doc_types", label: "Issue Creation" },
];

function parseListValue(value: unknown): string[] {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) return parsed;
        } catch { /* ignore */ }
    }
    return [];
}

/** Fire-and-forget save — no query invalidation */
function persistSetting(category: string, key: string, value: unknown) {
    apiPut(`/admin/settings/${category}/${key}`, { value })
        .then(() => {
            toast.success("Document Check Matrix aggiornata");
        })
        .catch(() => {
            toast.error("Salvataggio Document Check Matrix fallito");
        });
}

interface DocumentCheckMatrixProps {
    /** All settings for the extraction category (from parent CategorySettingsPanel) */
    extractionSettings: Setting[];
}

export function DocumentCheckMatrix({ extractionSettings }: DocumentCheckMatrixProps) {
    const { data: allDocTypes = [] } = useDocumentTypes();
    const fetchedRef = useRef(false);

    // --- Extract initial values from parent settings prop ---
    const initialState = useMemo(() => {
        const state: Record<ColumnKey, string[]> = {
            single_check: [],
            cross_check: [],
            issue_creation: [],
        };
        for (const col of COLUMNS) {
            const setting = extractionSettings.find((s) => s.key === col.settingKey);
            if (setting) {
                state[col.key] = parseListValue(setting.value);
            }
        }
        return state;
    }, [extractionSettings]);

    // --- Local state (initialized from props, then fully local) ---
    const [localState, setLocalState] = useState<Record<ColumnKey, string[]>>(initialState);
    const [crossCheckEnabled, setCrossCheckEnabled] = useState(true);

    // Re-sync when parent settings change (e.g., page load / scope change)
    useEffect(() => {
        setLocalState(initialState);
    }, [initialState]);

    // Fetch cross_check.enabled once on mount (different category, no useSetting)
    useEffect(() => {
        if (fetchedRef.current) return;
        fetchedRef.current = true;
        apiGet<{ value: unknown }>("/admin/settings/cross_check/enabled")
            .then((s) => {
                const v = s.value;
                if (typeof v === "boolean") setCrossCheckEnabled(v);
                else if (typeof v === "string") setCrossCheckEnabled(v === "true");
            })
            .catch(() => { /* setting may not exist — default true */ });
    }, []);

    // --- Filtered doc types (exclude hidden) ---
    const visibleDocTypes = useMemo(
        () =>
            allDocTypes
                .filter((dt) => !HIDDEN_DOC_TYPES.includes(dt.key))
                .sort((a, b) => a.name.localeCompare(b.name)),
        [allDocTypes]
    );

    // --- Handlers ---
    const toggleDocType = useCallback(
        (column: ColumnKey, docTypeKey: string) => {
            const current = localState[column];
            const next = current.includes(docTypeKey)
                ? current.filter((k) => k !== docTypeKey)
                : [...current, docTypeKey];
            const col = COLUMNS.find((c) => c.key === column)!;

            setLocalState((prev) => ({ ...prev, [column]: next }));
            persistSetting(col.settingCategory, col.settingKey, next);
        },
        [localState]
    );

    const selectAll = useCallback(
        (column: ColumnKey) => {
            const allKeys = visibleDocTypes.map((dt) => dt.key);
            setLocalState((prev) => ({ ...prev, [column]: allKeys }));
            const col = COLUMNS.find((c) => c.key === column)!;
            persistSetting(col.settingCategory, col.settingKey, allKeys);
        },
        [visibleDocTypes]
    );

    const deselectAll = useCallback(
        (column: ColumnKey) => {
            setLocalState((prev) => ({ ...prev, [column]: [] }));
            const col = COLUMNS.find((c) => c.key === column)!;
            persistSetting(col.settingCategory, col.settingKey, []);
        },
        []
    );

    const toggleCrossCheckMaster = useCallback(
        (isSelected: boolean) => {
            setCrossCheckEnabled(isSelected);
            persistSetting("cross_check", "enabled", isSelected);
        },
        []
    );

    // --- Column helpers ---
    const allVisibleKeys = useMemo(() => visibleDocTypes.map((dt) => dt.key), [visibleDocTypes]);

    const isAllSelected = useCallback(
        (column: ColumnKey) => {
            if (allVisibleKeys.length === 0) return false;
            return allVisibleKeys.every((k) => localState[column].includes(k));
        },
        [allVisibleKeys, localState]
    );

    const isNoneSelected = useCallback(
        (column: ColumnKey) => localState[column].length === 0,
        [localState]
    );

    if (visibleDocTypes.length === 0) return null;

    return (
        <div className="mt-6 space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-sm font-semibold text-primary">Document Check Matrix</h3>
                    <p className="text-xs text-tertiary mt-0.5">
                        Control which document types participate in each validation step.
                    </p>
                </div>
            </div>

            {/* Master Cross-Check Toggle */}
            <div className="flex items-center justify-between rounded-lg border border-secondary bg-secondary/30 px-4 py-3">
                <div>
                    <p className="text-sm font-medium text-primary">Cross-Check Validation</p>
                    <p className="text-xs text-tertiary">
                        Master toggle. When disabled, no cross-document validation runs for any container.
                    </p>
                </div>
                <Checkbox
                    isSelected={crossCheckEnabled}
                    onChange={toggleCrossCheckMaster}
                />
            </div>

            {/* Helper text for empty lists */}
            <p className="text-xs text-tertiary">
                No types selected in a column = all types enabled for that check (backwards compatible).
            </p>

            {/* Matrix table */}
            <div className="border border-secondary rounded-lg overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="bg-secondary">
                            <th className="text-left text-xs text-tertiary font-medium uppercase px-4 py-2.5 w-1/3">
                                Document Type
                            </th>
                            {COLUMNS.map((col) => (
                                <th key={col.key} className="text-center text-xs text-tertiary font-medium uppercase px-4 py-2.5">
                                    <div className="flex flex-col items-center gap-1.5">
                                        <span>{col.label}</span>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                className="text-[10px] text-brand-secondary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                                                onClick={() => selectAll(col.key)}
                                                disabled={
                                                    isAllSelected(col.key) ||
                                                    (col.key === "cross_check" && !crossCheckEnabled)
                                                }
                                            >
                                                All
                                            </button>
                                            <span className="text-quaternary">|</span>
                                            <button
                                                type="button"
                                                className="text-[10px] text-brand-secondary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                                                onClick={() => deselectAll(col.key)}
                                                disabled={
                                                    isNoneSelected(col.key) ||
                                                    (col.key === "cross_check" && !crossCheckEnabled)
                                                }
                                            >
                                                None
                                            </button>
                                        </div>
                                        {!isNoneSelected(col.key) && (
                                            <Badge color="brand" size="sm">
                                                {localState[col.key].length}
                                            </Badge>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {visibleDocTypes.map((dt, idx) => (
                            <tr
                                key={dt.key}
                                className={idx % 2 === 0 ? "bg-primary" : "bg-secondary/20"}
                            >
                                <td className="px-4 py-2 text-sm text-primary">
                                    {dt.name}
                                </td>
                                {COLUMNS.map((col) => {
                                    const isDisabled =
                                        col.key === "cross_check" && !crossCheckEnabled;
                                    const isChecked = localState[col.key].includes(dt.key);

                                    return (
                                        <td key={col.key} className="px-4 py-2 text-center">
                                            <div className="flex justify-center">
                                                <button
                                                    type="button"
                                                    onClick={() => !isDisabled && toggleDocType(col.key, dt.key)}
                                                    disabled={isDisabled}
                                                    className="focus:outline-none disabled:cursor-not-allowed"
                                                >
                                                    <CheckboxBase
                                                        isSelected={isChecked}
                                                        isDisabled={isDisabled}
                                                    />
                                                </button>
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
